import { cva, type VariantProps } from "class-variance-authority"
import { AnimatePresence, motion } from "framer-motion"
import { forwardRef } from "react"
import { twMerge } from "tailwind-merge"

const input = cva(
  [
    "peer",
    "block",
    "w-full",
    "rounded-md",
    "bg-background",
    "border",
    "border-input",
    "px-3",
    "py-1",
    "text-sm",
    "dark:text-gray-100",
    "outline-2",
    "placeholder:text-muted-foreground",
    "ring-offset-background",
    "file:border-0",
    "file:bg-transparent",
    "file:text-sm",
    "file:font-medium",
    "focus-visible:outline-none",
    "h-10",
    "focus-visible:ring-1",
    "focus-visible:ring-ring",
    "focus-visible:ring-offset-2",
    "dark:focus-visible:ring-gray-600",
    "disabled:cursor-not-allowed",
    "disabled:opacity-50",
  ],
  {
    variants: {
      intent: {
        default: ["border-gray-200 dark:border-gray-800"],
        disabled: ["bg-secondary-500", "text-gray-400", "border-transparent", "w-full", "cursor-not-allowed"],
      },
    },
    defaultVariants: {
      intent: "default",
    },
  }
)

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement>, VariantProps<typeof input> {
  label?: string
  touched?: boolean
  error?: string
  suffix?: React.ReactNode
  startWithSuffix?: boolean
  layout?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, layout, error, className, intent, suffix, startWithSuffix, ...props }, ref) => {
    return (
      <div className={layout}>
        {label && <label className="text-secondary-700 block py-1 text-sm font-medium">{label}</label>}
        <div className="relative flex items-center">
          <input
            className={twMerge(
              input({ intent, className }),
              [error && "border-red-500 dark:border-red-500"],
              [startWithSuffix && "ml-4"]
            )}
            ref={ref}
            {...props}
          />
          {suffix && (
            <div className={`absolute inset-y-0 flex items-center pr-4 ${startWithSuffix ? "-left-1" : "right-0"}`}>
              {suffix}
            </div>
          )}
        </div>
        <AnimatePresence>
          {error && (
            <div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 100 }}
                transition={{ ease: "easeOut", duration: 0.2 }}
                className="mt-1 pl-1 text-sm text-red-500"
              >
                {error}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    )
  }
)

Input.displayName = "Input"
